import Component from '@glimmer/component';
import { getOwner } from '@ember/owner';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import { task } from 'ember-concurrency';

export const MESSAGE_MAX_LENGTH = 1000;

const ERROR_MESSAGES = {
  403: 'Messages to this show are not being accepted right now.',
  429: 'You have sent a lot of messages. Wait a few minutes and try again.',
};

const FALLBACK_ERROR = 'Your message did not go through. Try again in a moment.';

export default class MessageButtonComponent extends Component {
  @tracked isOpen = false;
  @tracked isSent = false;
  @tracked airing;
  @tracked senderName = '';
  @tracked body = '';
  @tracked selectedIds = [];
  @tracked errorMessage;

  website = '';
  openedAt = 0;

  maxLength = MESSAGE_MAX_LENGTH;
  headingId = `${guidFor(this)}-heading`;

  // Only the embeddable takes listener messages, so the staff app has no such service.
  get listenerMessages() {
    return getOwner(this).lookup('service:listener-messages');
  }

  get recipients() {
    return this.listenerMessages?.recipientsFor(this.airing ?? this.args.feed?.airing) ?? [];
  }

  get hasSeveralRecipients() {
    return this.recipients.length > 1;
  }

  get buttonLabel() {
    return 'Send message';
  }

  get heading() {
    return this.hasSeveralRecipients ? 'Message the hosts' : `Message ${this.recipients[0]?.name}`;
  }

  get isPreview() {
    return !!this.listenerMessages?.isPreview;
  }

  get sendsToEveryone() {
    return this.selectedIds.length === 0;
  }

  get canSend() {
    return this.body.trim().length > 0 && !this.isPreview && !this.sendTask.isRunning;
  }

  isSelected = (recipient) => this.selectedIds.includes(recipient.id);

  @action
  open(event) {
    this.trigger = event.currentTarget;
    this.airing = this.args.feed?.airing;
    this.senderName = this.listenerMessages.senderName;
    this.body = '';
    this.selectedIds = [];
    this.errorMessage = null;
    this.isSent = false;
    this.website = '';
    this.openedAt = Date.now();
    this.isOpen = true;
  }

  @action
  close() {
    this.isOpen = false;
    this.airing = null;
    this.trigger?.focus();
  }

  @action
  focusMessage(element) {
    element.querySelector('textarea, [data-autofocus]')?.focus();
  }

  @action
  closeOnEscape(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  }

  @action
  updateSenderName(event) {
    this.senderName = event.target.value;
  }

  @action
  updateWebsite(event) {
    this.website = event.target.value;
  }

  @action
  updateBody(event) {
    this.body = event.target.value.slice(0, MESSAGE_MAX_LENGTH);
  }

  @action
  sendToEveryone() {
    this.selectedIds = [];
  }

  @action
  toggleRecipient(recipient) {
    this.selectedIds = this.isSelected(recipient)
      ? this.selectedIds.filter((id) => id !== recipient.id)
      : [...this.selectedIds, recipient.id];
  }

  @action
  submit(event) {
    event.preventDefault();
    if (!this.canSend) return;
    this.sendTask.perform();
  }

  sendTask = task({ drop: true }, async () => {
    this.errorMessage = null;
    try {
      await this.listenerMessages.send(this.airing, {
        body: this.body.trim(),
        senderName: this.senderName.trim(),
        to: this.selectedIds,
        website: this.website,
        openMs: Date.now() - this.openedAt,
        heardAt: this.heardAt,
      });
      this.isSent = true;
    } catch (error) {
      this.errorMessage = this.messageFor(error);
    }
  });

  // Someone playing a recording writes about what they're hearing, so the message belongs at the playhead.
  get heardAt() {
    let feed = this.args.feed;
    if (feed?.kind !== 'playback' || feed.airing !== this.airing) return null;
    let startsAt = this.airing?.actualStartsAt || this.airing?.startsAt;
    if (!startsAt || feed.playheadPositionMs == null) return null;
    return new Date(new Date(startsAt).getTime() + feed.playheadPositionMs).toISOString();
  }

  messageFor(error) {
    if (error?.status === 422) return error.serverMessage || FALLBACK_ERROR;
    return ERROR_MESSAGES[error?.status] ?? FALLBACK_ERROR;
  }
}
